import React, {useContext, useEffect, useCallback, useRef} from "react";
import AppRouter from "./components/AppRouter";
import {BrowserRouter as Router} from "react-router-dom";
import {observer} from "mobx-react-lite";
import {check, userInfo} from "./http/userAPI";
import {Context} from ".";
import {authService} from "./http/authService";
import {authEvents} from "./http/authEvents";
import {getApiErrorMessage} from "./http/apiError";
import HomeHeader from "./components/HomeHeader";
import CssBaseline from "@mui/material/CssBaseline";
import { createTheme } from '@mui/material/styles';
import {ThemeProvider, Box} from "@mui/material";
import Footer from "./components/Footer";
import { SnackbarProvider } from "./components/snackbar/SnackbarProvider";
import WsAPI from './http/wsAPI';
//import WsUserAPI from "./http/wsUserAPI";
import './updateChecker';

const App = observer(() => {
    const {user} = useContext(Context);
    const appliedTokenRef = useRef(null);

    const resetAuth = useCallback(() => {
        appliedTokenRef.current = null;
        authService.clearAccessToken();
        user.setIsAuth(false);
        user.setUserName('');
        user.setRole('');
        user.setUser({});
        user.setUserInfo({});
        user.setIsLoading(false);
    }, [user]);

    const applyAuthData = useCallback(async (data) => {
        if (!data?.access_token) {
            resetAuth();
            return;
        }

        appliedTokenRef.current = data.access_token;
        authService.setAccessToken(data.access_token);
        user.setUser(data);

        const decoded = authService.getPayload();
        if (!decoded) {
            resetAuth();
            return;
        }

        user.setIsAuth(true);
        user.setUserName(decoded.name);
        user.setRole(decoded.role);

        try {
            const info = await userInfo();
            user.setUserInfo(info);
        } catch (e) {
            console.error(getApiErrorMessage(e, 'Не удалось загрузить данные пользователя'));
        } finally {
            user.setIsLoading(false);
        }
    }, [resetAuth, user]);


    useEffect(() => {
        const token = user.user?.access_token;
        if (!token || appliedTokenRef.current === token) {
            return;
        }

        applyAuthData(user.user);
    }, [user.user, applyAuthData]);

    useEffect(() => {
        const unsubscribe = authEvents.onLogout(resetAuth);

        check()
            .then(applyAuthData)
            .catch((e) => {
                if (e.status === 400 || e.status === 401 || e.isAuthError) {
                    resetAuth();
                    return;
                }

                user.setIsLoading(false);
                console.error(getApiErrorMessage(e, 'Не удалось проверить сессию'));
            });

        return unsubscribe;
    }, [applyAuthData, resetAuth, user]);

    useEffect(() => {
        const payload = authService.getPayload();
        if (!payload?.exp) {
            return undefined;
        }

        const now = Math.floor(Date.now() / 1000);
        const refreshInMs = Math.max((payload.exp - now - 60) * 1000, 0);

        const timerId = setTimeout(() => {
            check()
                .then(applyAuthData)
                .catch(resetAuth);
        }, refreshInMs);

        return () => clearTimeout(timerId);
    }, [user.user, applyAuthData, resetAuth]);

    const theme = createTheme({
        palette: {
            mode: "dark",
            primary: {
                main: '#00c58a',
                contrastText: '#ffffff'
            },
            secondary: {
                main: '#ff5722',
            },
            background: {
                default: '#2e3334',
                paper: '#384042'
            }
        },
        components: {
            MuiTextField: {
                styleOverrides: {
                    root: {
                        background: "#363f41"
                    }
                }
            },
            typography: {
                styleOverrides: {
                    root: {},
                },
                chip: {
                    root: {
                        backgroundColor: '#ffffff'
                    }
                }
            }
        }
    });

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />

            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column"
                }}
            >
                <SnackbarProvider>
                    <Router>
                        <HomeHeader />

                        {user.isAuth && <WsAPI />}

                        <Box component="main" sx={{ flexGrow: 1, mb: 3 }}>
                            <AppRouter />
                        </Box>

                        <Footer />
                    </Router>
                </SnackbarProvider>
            </Box>
        </ThemeProvider>
    );
});

export default App;
